import { useCallback, useEffect, useState } from "react";
import { useAuthStore } from "@/store/authStore";
import { readStoredIds, writeStoredIds } from "@/lib/storedIds";

const MAX_RECENT_OPERATIONS = 5;
const RECENT_OPERATIONS_KEY_PREFIX = "forgepoint.recentOperations.";

interface RecentOperations {
  /** Recently selected operation ids, most recent first. */
  recentOperationIds: string[];
  /** Records an operation as the most recently used one. */
  pushRecentOperation: (operationId: string) => void;
}

/**
 * Tracks the operations the signed-in user most recently picked, persisted to
 * localStorage under a per-user key.
 * @returns The recent operation ids and a handler to record a new one
 */
export function useRecentOperations(): RecentOperations {
  const userId = useAuthStore((state) => state.user?.id);
  const storageKey = userId ? `${RECENT_OPERATIONS_KEY_PREFIX}${userId}` : null;
  const [recentOperationIds, setRecentOperationIds] = useState<string[]>([]);

  useEffect(() => {
    setRecentOperationIds(storageKey ? (readStoredIds(storageKey) ?? []) : []);
  }, [storageKey]);

  const pushRecentOperation = useCallback(
    (operationId: string) => {
      if (!storageKey) {
        return;
      }
      const next = [
        operationId,
        ...recentOperationIds.filter((id) => id !== operationId),
      ].slice(0, MAX_RECENT_OPERATIONS);
      writeStoredIds(storageKey, next);
      setRecentOperationIds(next);
    },
    [recentOperationIds, storageKey],
  );

  return { recentOperationIds, pushRecentOperation };
}
